import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '../../../services/api.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ShareModalComponent } from '../../../shared/components/share-modal/share-modal.component';
import { RewardModalComponent } from '../../../shared/components/reward-modal/reward-modal.component';
import { NavBarComponent } from '../../../shared/components/nav-bar/nav-bar.component';
import { FormsModule } from '@angular/forms';
import { PositionSort } from '../../../common/constants/common.constants';
import { ChartComponent } from '../chart/chart.component';
import { DropdownPlacement, measureDropdownPlacement, PageScrollLock } from '../../../common/utils/dropdown-viewport';
import { ActivityTrade, parseActivityTrade } from '../../../common/utils/activity-trade';
import { shortenWalletAddress } from '../../../common/utils/wallet-address';

/** Hex characters an activity row keeps after the 0x, e.g. 0x0f51...2ceb */
const ACTIVITY_ADDRESS_HEX = 4;

@Component({
  selector: 'app-leaderboard-details',
  imports: [CommonModule, ShareModalComponent, RewardModalComponent, NavBarComponent, FormsModule, ChartComponent],
  templateUrl: './leaderboard-details.component.html',
  styleUrl: './leaderboard-details.component.css'
})
export class LeaderboardDetailsComponent implements OnInit, OnDestroy {
  activeMainTab: 'positions' | 'activity' = 'positions';
  activeStatus: 'active' | 'closed' = 'active';
  isShareModalOpen = false;
  isRewardModalOpen = false
  isValueOpen = false;
  isAmtOpen = false;
  selectedValue = 'Value';
  selectedAmount = 'Min amount';
  valueMenu: DropdownPlacement = { maxHeight: 320, above: false };

  @ViewChild('valueTrigger') valueTrigger?: ElementRef<HTMLElement>;
  private scrollLock = new PageScrollLock();

  values = [
    { label: 'Value', value: PositionSort.PAYOUT },
    { label: 'Profit/Loss %', value: PositionSort.PROFIT_LOSS_PCT },
    { label: 'Bet', value: PositionSort.BET },
    { label: 'Alphabetically', value: PositionSort.MARKET_NAME },
    { label: 'Shares', value: PositionSort.SHARES },
    { label: 'Ending soon', value: PositionSort.ENDING_SOON }
  ];

  amounts = ['None', '$10', '$100', '$1,000', '$10,000', '$100,000',];

  userId: string | null = null;
  positions: any[] = [];
  activities: any[] = [];
  loading = false;
  activitiesLoading = false;
  searchQuery = '';
  profitHistoryData: any[] = [];
  activeProfitInterval = 'ALL';
  currentProfitLossValue = '0.00';
  private searchSubject = new Subject<string>();

  constructor(
    private elementRef: ElementRef,
    private route: ActivatedRoute,
    private router: Router,
    private api: ApiService
  ) {
    this.searchSubject.pipe(
      debounceTime(500),
      distinctUntilChanged()
    ).subscribe(query => {
      this.searchQuery = query;
      this.fetchPositions();
    });
  }

  tradingStats: any = null;
  userDetails: any = null;

  get profilePicture(): string {
    return this.tradingStats?.profile_picture || '';
  }

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      this.userId = params['user_id'] || params['id'];
      // console.log('LeaderboardDetailsComponent: userId from queryParams:', this.userId, 'Params:', params);
      if (this.userId) {
        this.fetchPositions();
        this.fetchTradingStats();
        this.fetchUserProfile();
        this.loadProfitHistory(this.activeProfitInterval);
      }
    });
  }

  get profitChartData() {
    return {
      yes: this.profitHistoryData,
      no: []
    };
  }

  formatCurrency(value: string | number | undefined | null, showPlus: boolean = false): string {
    if (value === null || value === undefined || value === '') return '$0.00';

    const cleanValue = value.toString().replace('$', '').replace('+', '');
    if (cleanValue.startsWith('-')) {
      return '-$' + cleanValue.slice(1);
    }

    return (showPlus ? '+' : '') + '$' + cleanValue;
  }

  loadProfitHistory(interval: string): void {
    if (!this.userId) return;

    this.activeProfitInterval = interval;
    this.api.getUserProfitHistory(this.userId, interval).subscribe({
      next: (res: any) => {
        const rawPoints = (res.data?.points || []).map((point: any) => ({
          time: Math.floor(new Date(point.timestamp).getTime() / 1000),
          value: parseFloat(point.profit || 0)
        })).sort((a: any, b: any) => a.time - b.time);

        if (rawPoints.length === 0) {
          const now = Math.floor(Date.now() / 1000);
          this.profitHistoryData = [
            { time: now - 86400, value: 0 },
            { time: now, value: 0 }
          ];
        } else {
          const firstTime = rawPoints[0].time;
          this.profitHistoryData = [
            { time: firstTime - 86400, value: 0 },
            ...rawPoints
          ];
        }

        const totalProfitNum = parseFloat(res?.data?.total_profit ?? '0');
        const prefix = totalProfitNum >= 0 ? '+' : '-';
        this.currentProfitLossValue = `${prefix}$${Math.abs(totalProfitNum).toFixed(2)}`;
      },
      error: (err) => {
        console.error('loadProfitHistory error:', err);
        this.profitHistoryData = [];
        this.currentProfitLossValue = '0.00';
      }
    });
  }

  setProfitInterval(interval: string): void {
    this.loadProfitHistory(interval);
  }

  fetchUserProfile() {
    if (!this.userId) return;
    this.api.getUserProfile(this.userId).subscribe({
      next: (res) => {
        this.userDetails = {
          ...res.data,
          profile_picture: res.data.profile_picture
        };
      },
      error: (err) => console.error('fetchUserProfile error:', err)
    });
  }

  fetchTradingStats() {
    if (!this.userId) return;
    this.api.getUserTradingStats(this.userId).subscribe({
      next: (res) => {
        this.tradingStats = {
          ...res.data,
          profile_picture: res.data.profile_picture
        };
        // Patch: Create name field from user_name for easier access
        if (this.tradingStats?.user_name) {
          this.tradingStats.name = this.tradingStats.user_name;
        }
      },
      error: (err) => console.error('fetchTradingStats error:', err)
    });
  }

  fetchPositions() {
    if (!this.userId) {
      console.warn('fetchPositions: No userId found');
      return;
    }

    // console.log('fetchPositions: Fetching for', this.userId, 'status:', this.activeStatus);
    this.loading = true;
    const selectedSort = this.values.find(v => v.label === this.selectedValue)?.value;

    const sortBy = selectedSort && selectedSort !== PositionSort.PAYOUT ? selectedSort : undefined;

    // Send 'ALL' when viewing active positions (backend doesn't support 'ACTIVE' filter)
    // Send 'CLOSED' only when explicitly viewing closed positions
    const apiStatus = this.activeStatus === 'closed' ? 'CLOSED' : 'ACTIVE';

    this.api.getUserPositions(
      this.userId,
      this.searchQuery,
      sortBy,
      1, // page
      100, // limit
      apiStatus
    ).subscribe({
      next: (res) => {
        console.log('fetchPositions: Success', res.data.results?.length, 'items');
        this.positions = (res.data.results || []).map((pos: any) => {
          const avgPrice = parseFloat(pos.avg_price || pos.avg_cost_per_share || '0');
          const currentPrice = parseFloat(pos.current_price || pos.latest_price || '0');

          // If price is > 1, it's likely already in cents (e.g. 50.00 instead of 0.50)
          const normalizedAvg = avgPrice > 1 ? avgPrice / 100 : avgPrice;
          const normalizedCurrent = currentPrice > 1 ? currentPrice / 100 : currentPrice;

          return {
            ...pos,
            market_image_url: pos.market_image_url || pos.image_url || pos.market_image || pos.image || '',
            outcome_title: pos.outcome_title || pos.outcome || pos.result || 'Unknown',
            avg_price_numeric: normalizedAvg,
            current_price_numeric: normalizedCurrent,
            value_numeric: parseFloat(pos.value || '0'),
            total_pnl_numeric: parseFloat(pos.total_pnl || pos.profit_loss || '0')
          };
        });
        this.loading = false;
      },
      error: (err) => {
        console.error('fetchPositions: Error', err);
        this.loading = false;
      }
    });
  }

  onSearchChange() {
    this.searchSubject.next(this.searchQuery);
  }

  navigateToMarket(position: any): void {
    const marketId = position?.market_id;
    if (!marketId) {
      console.warn('navigateToMarket: Market ID is missing for position', position);
      return;
    }

    this.router.navigate(['/details', marketId]);
  }

  fetchActivities() {
    if (!this.userId) {
      console.warn('fetchActivities: No userId found');
      return;
    }

    // console.log('fetchActivities: Fetching for', this.userId);
    this.activitiesLoading = true;
    this.api.getMarketActivity(
      null, // marketId
      this.userId,
      1, // page
      100 // limit
    ).subscribe({
      next: (res) => {
        // console.log('fetchActivities: Success', res.data.results?.length, 'items');
        this.activities = (res.data.results || []).map((item: any) => ({
          ...item,
          market_image_url: item.market_image_url || item.image_url || item.market_image || item.image || '',
          outcome_title: item.outcome_title || item.outcome || item.result || '',
          size: item.size || item.shares || item.quantity || 0,
          amount: item.amount || item.total_value || 0
        }));
        this.activitiesLoading = false;
      },
      error: (err) => {
        console.error('fetchActivities: Error', err);
        this.activitiesLoading = false;
      }
    });
  }

  onMainTabChange(tab: 'positions' | 'activity') {
    // console.log('onMainTabChange: switching to', tab);
    this.activeMainTab = tab;
    if (tab === 'positions') {
      this.fetchPositions();
    } else {
      this.fetchActivities();
    }
  }

  /**
   * The name an activity row is filed under. A wallet address is cut to
   * 0x0f51...2ceb - four hex characters is as much as the row has width for -
   * while a chosen username is left as it is.
   * @param item - The activity record the API returned
   */
  getActivityActorName(item: any): string {
    const name = item?.user_name || item?.username || item?.name || item?.activity_text?.split(' ')[0] || 'User';

    return shortenWalletAddress(name, ACTIVITY_ADDRESS_HEX) || name;
  }

  /**
   * The trade behind one activity row - quantity, outcome, the price of a single
   * share and the total, each named separately so the row can say which is which.
   * Read from the sentence itself, since a market name of any length would shift
   * every position in a plain word split.
   * @param item - The activity record the API returned
   */
  getActivityTrade(item: any): ActivityTrade {
    return parseActivityTrade(item?.activity_text || '');
  }

  onStatusChange(status: 'active' | 'closed') {
    console.log('onStatusChange: switching to', status);
    this.activeStatus = status;
    this.fetchPositions();
  }

  selectValue(value: any) {
    this.selectedValue = value.label;
    this.closeDropdowns();
    this.fetchPositions();
  }

  /** Opens the sort menu, measured against the room left around its button */
  toggleValueDropdown() {
    const open = !this.isValueOpen;
    this.closeDropdowns();
    this.isValueOpen = open;

    if (open) {
      this.updateValueMenuPlacement();
      this.scrollLock.set(true);
    }
  }

  /** Shuts the dropdowns and gives the page its scrolling back */
  private closeDropdowns() {
    this.isValueOpen = false;
    this.isAmtOpen = false;
    this.scrollLock.set(false);
  }

  private updateValueMenuPlacement() {
    const trigger = this.valueTrigger?.nativeElement;
    if (trigger) {
      this.valueMenu = measureDropdownPlacement(trigger);
    }
  }

  /**
   * Rotating the phone or resizing the window changes the room around the
   * button, so an open menu is re-measured against the new viewport
   */
  @HostListener('window:resize')
  onWindowResize() {
    if (this.isValueOpen) {
      this.updateValueMenuPlacement();
    }
  }

  ngOnDestroy() {
    // Leaving the page with the menu open must not strand the lock on <body>
    this.closeDropdowns();
  }

  openShareModal() {
    this.isShareModalOpen = true;
  }

  closeShareModal() {
    this.isShareModalOpen = false;
  }
  openRewardModal() {
    this.isRewardModalOpen = true;
  }

  closeRewardModal() {
    this.isRewardModalOpen = false;
  }
  selectAmount(amount: string) {
    this.selectedAmount = amount;
    this.isAmtOpen = false;
    if (this.activeMainTab === 'activity') {
      this.fetchActivities();
    }
  }
  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.closeDropdowns();
    }
  }

  truncateUsername(name: string | undefined | null): string {
    if (!name) return '';
    if (name.startsWith('0x') && name.length > 10) {
      return `${name.slice(0, 6)}...${name.slice(-4)}`;
    }
    return name;
  }
}
